import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  RentCar,
  RentCarAvailableData,
  RentCarBookingHistory,
  RentCarBookingHistoryData,
  RentCarBookingHistoryParams,
  RentCarData,
  RentCarParams,
} from '@/libs/interfaces'
import { rentCarAPIMock } from '@/libs/mocks/handlers/rentCar'

const rentCarAPIReal = {
  create: async (body: Partial<RentCar>): Promise<CreateApiResult<Partial<RentCar>>> => {
    return await api
      .post<ApiResponse<Partial<RentCar>>>(URLs.RentCar.create(body.storeID || ''), body)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  delete: async (params: Partial<RentCar>): Promise<RentCar> => {
    const { storeID, rentCarRegNum } = params
    const response = await api.delete<ApiResponse<RentCar>>(
      URLs.RentCar.byRegNum(storeID || '', rentCarRegNum || ''),
    )
    return response.data?.data || []
  },
  getAvailableRentCars: async (params: RentCarParams): Promise<RentCarAvailableData> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api.get<ApiResponse<RentCarAvailableData>>(
      URLs.RentCar.availableRentCars(storeID || '', query),
    )
    return response.data?.data || []
  },
  getByRegNum: async (params: Partial<RentCar>): Promise<RentCar> => {
    const { storeID, rentCarRegNum } = params
    const response = await api.get<ApiResponse<RentCar>>(
      URLs.RentCar.byRegNum(storeID || '', rentCarRegNum || ''),
    )
    return response.data?.data || []
  },
  listCursor: async (params: RentCarParams): Promise<CursorPaginatedResponse<RentCar>> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<RentCarData>>(URLs.RentCar.list(storeID || '', query))
      .then((response) => response?.data?.data)

    return {
      data: response.cars || [],
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  listHistoryCursor: async (
    params: RentCarBookingHistoryParams,
  ): Promise<CursorPaginatedResponse<RentCarBookingHistory>> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<RentCarBookingHistoryData>>(URLs.RentCar.listHistory(storeID || '', query))
      .then((response) => response?.data?.data)

    return {
      data: response.bookings || [],
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: Partial<RentCar>): Promise<RentCar> => {
    const { storeID, rentCarRegNum } = payload
    const response = await api.patch<ApiResponse<RentCar>>(
      URLs.RentCar.byRegNum(storeID || '', rentCarRegNum || ''),
      payload,
    )
    return response.data?.data || []
  },
}

export const rentCarAPI: typeof rentCarAPIReal = __IS_MOCK_MODE__ ? rentCarAPIMock : rentCarAPIReal
